import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import { onlineApi } from '@/api/online.api';
import useDebouncedValue from '@/shared/hooks/useDebouncedValue';
import { useAppSelector } from '@/store/store';

import type { ClientFormValues } from '../types/client-form';

interface IProps {
  submitRequest: (values: ClientFormValues) => Promise<boolean>;
  handleNext: () => void;
}

const DEALER_GROUP_ID_KEY = 'dealerGroupId';
const LEASING_DEALER_GROUP_ID_KEY = 'leasingDealerGroupId';

const useClientForm = ({ submitRequest, handleNext }: IProps) => {
  const dealerGroup = useAppSelector((state) => state.app.dealerGroup);
  const isLeasing = useAppSelector(
    (state) => state.app.productType === 'leasing'
  );

  const methods = useForm<ClientFormValues, unknown>({
    mode: 'all',
    delayError: 200,
    defaultValues: {
      personalId: '',
      fullName: '',
      phone: '',
      policyAgreement: false
    }
  });
  const values = methods.watch();
  const { setValue, watch } = methods;

  const [submitLoading, setSubmitLoading] = useState(false);
  const [clientError, setClientError] = useState(false);

  const personalId = watch('personalId');
  const debouncedPersonalId = useDebouncedValue(personalId);

  useEffect(() => {
    const checkClient = async () => {
      const dealerGroupIdForVerify = isLeasing
        ? localStorage.getItem(LEASING_DEALER_GROUP_ID_KEY) ||
          localStorage.getItem(DEALER_GROUP_ID_KEY) ||
          dealerGroup?.uuid
        : dealerGroup?.uuid;
      if (
        debouncedPersonalId &&
        debouncedPersonalId.length === 14 &&
        dealerGroupIdForVerify
      ) {
        try {
          const response = await onlineApi.verifyClient(
            debouncedPersonalId,
            dealerGroupIdForVerify
          );
          if (response.exists && response.client) {
            const { first_name, last_name, middle_name, phone_number } =
              response.client;
            const fullName = `${last_name} ${first_name} ${middle_name}`.trim();

            setValue('fullName', fullName);
            setValue('phone', phone_number);
          }
        } catch (error) {
          console.error(error);
        }
      }
    };

    checkClient();
  }, [debouncedPersonalId, setValue, dealerGroup, isLeasing]);

  useEffect(() => {
    // Сбрасываем ошибку при изменении полей
    if (clientError) {
      setClientError(false);
    }
  }, [values]);

  const handleClientFormSubmit = async (values: ClientFormValues) => {
    setSubmitLoading(true);
    const response = await submitRequest(values);
    if (response) {
      handleNext();
    } else {
      setClientError(true);
    }
    setSubmitLoading(false);
  };

  return {
    clientFormMethods: methods,
    clientFormLoading: submitLoading,
    clientError,
    handleClientFormSubmit
  };
};

export default useClientForm;
